/**
 * MessagesPage Component - Complete Updated Version
 * Enhanced with file attachments, emojis, improved UI, and mobile responsiveness
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  Search, 
  Loader, 
  AlertCircle, 
  MessageSquare, 
  Send,
  Check,
  CheckCheck,
  Image,
  Paperclip,
  ArrowLeft,
  Info,
  Phone,
  Video,
  MoreHorizontal,
  Smile,
  Mic,
  X,
  File,
  Download
} from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';
import messagingService from '../services/messagingService';
import EnhancedMessageInput from '../components/EnhancedMessageInput';
import MessageAttachment from '../components/MessageAttachment';
import UnconnectedUserBanner from '../components/UnconnectedUserBanner';
import { toast } from 'react-hot-toast';

/**
 * MessagesPage Component - Complete with file attachments and enhanced features
 * 
 * @returns {JSX.Element} Messages page component
 */
const MessagesPage = () => {
  const { conversationId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // State for conversations list
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsError, setConversationsError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for active conversation
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [showStickyDate, setShowStickyDate] = useState(false);
  const [stickyDate, setStickyDate] = useState('');
  const [showUnconnectedWarning, setShowUnconnectedWarning] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const lastScrollPosition = useRef(0);
  const scrollTimeoutRef = useRef(null);
  
  // Load conversations on mount
  useEffect(() => {
    if (!currentUser) return;

    setConversationsLoading(true);
    
    // Subscribe to conversations
    const unsubscribe = messagingService.subscribeToConversations(
      currentUser.uid,
      (updatedConversations) => {
        setConversations(updatedConversations);
        setConversationsLoading(false);
        
        // If conversationId exists, set the active conversation
        if (conversationId) {
          const conversation = updatedConversations.find(c => c.id === conversationId);
          if (conversation) {
            setActiveConversation(conversation);
            
            // Check security info
            const securityInfo = messagingService.getConversationSecurityInfo(
              conversation, 
              currentUser.uid
            );
            
            setShowUnconnectedWarning(securityInfo.shouldShowWarning);
            setIsBlocked(securityInfo.isBlocked);
          }
        }
      }
    );
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [currentUser, conversationId]);
  
  // Load active conversation when conversationId changes
  useEffect(() => {
    if (!currentUser || !conversationId) {
      setActiveConversation(null);
      setMessages([]);
      return;
    }
    
    setMessagesLoading(true);
    
    // Find conversation in already loaded conversations
    const conversation = conversations.find(conv => conv.id === conversationId);
    if (conversation) {
      setActiveConversation(conversation);
    }
    
    // Subscribe to messages
    const messagesUnsubscribe = messagingService.subscribeToMessages(
      conversationId,
      (newMessages) => {
        setMessages(newMessages);
        setMessagesLoading(false);
      }
    );
    
    // Mark messages as read when conversation is opened
    messagingService.markMessagesAsRead(conversationId)
      .catch(err => console.error('Error marking messages as read:', err));
    
    return () => {
      if (typeof messagesUnsubscribe === 'function') {
        messagesUnsubscribe();
      }
    };
  }, [currentUser, conversationId, conversations]);
  
  // Mark messages as read when they're received
  useEffect(() => {
    if (activeConversation && messages.length > 0 && conversationId) {
      messagingService.markMessagesAsRead(conversationId)
        .catch(err => console.error('Error marking messages as read:', err));
    }
  }, [messages.length, conversationId, activeConversation]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Focus input when active conversation changes
  useEffect(() => {
    if (activeConversation) {
      inputRef.current?.focus();
    }
  }, [activeConversation]);
  
  // Filter conversations based on search query
  const filteredConversations = conversations.filter(conversation => {
    if (!searchQuery.trim()) return true;
    
    const otherParticipantId = conversation.participants.find(
      id => id !== currentUser?.uid
    );
    
    const participantInfo = conversation.participantsInfo?.[otherParticipantId] || {};
    
    return (
      participantInfo.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      participantInfo.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conversation.lastMessage?.content?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Handle scroll in message container to show sticky date header
  const handleMessagesScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Detect scroll direction
    const scrollPosition = container.scrollTop;
    const isScrollingUp = scrollPosition < lastScrollPosition.current;
    lastScrollPosition.current = scrollPosition;
    
    // Find which date header is currently visible
    if (messages.length > 0) {
      const elements = container.querySelectorAll('[data-message-date]');
      if (elements.length > 0) {
        let currentDateEl = null;
        
        // Find the last date header that has passed the top of the viewport
        for (let i = 0; i < elements.length; i++) {
          const el = elements[i];
          const rect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          if (rect.top < containerRect.top + 20) {
            currentDateEl = el;
          } else {
            break;
          }
        }
        
        if (currentDateEl) {
          const date = currentDateEl.getAttribute('data-message-date');
          setStickyDate(date);
          setShowStickyDate(true);
          
          // Hide the sticky date after 1.5 seconds of no scrolling
          clearTimeout(scrollTimeoutRef.current);
          scrollTimeoutRef.current = setTimeout(() => {
            setShowStickyDate(false);
          }, 1500);
        }
      }
    }
  }, [messages.length]);
  
  // Set up scroll listener for message container
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleMessagesScroll);
      return () => {
        container.removeEventListener('scroll', handleMessagesScroll);
        clearTimeout(scrollTimeoutRef.current);
      };
    }
  }, [handleMessagesScroll]);
  
  // Get other participant info from conversation
  const getOtherParticipant = (conversation) => {
    if (!conversation || !currentUser) return null;
    
    const otherParticipantId = conversation.participants.find(
      id => id !== currentUser.uid
    );
    
    return {
      id: otherParticipantId,
      ...conversation.participantsInfo?.[otherParticipantId]
    };
  };
  
  // Format simple message time (HH:MM)
  const formatSimpleTime = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  };

  // Format date for message groups
  const formatMessageDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString(undefined, { 
          weekday: 'long', 
          month: 'long', 
          day: 'numeric'
        });
      }
    } catch (error) {
      return '';
    }
  };
  
  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};
    
    messages.forEach(message => {
      if (!message.timestamp) return;
      
      const date = new Date(message.timestamp);
      const dateString = date.toDateString();
      
      if (!groups[dateString]) {
        groups[dateString] = [];
      }
      
      groups[dateString].push(message);
    });
    
    return Object.entries(groups).map(([dateString, msgs]) => ({
      date: new Date(dateString),
      messages: msgs,
    }));
  };
  
  // Get read status icon
  const getReadStatusIcon = (conversation) => {
    if (!conversation?.lastMessage) return null;
    
    // Only show read status for messages sent by current user
    if (conversation.lastMessage.senderId !== currentUser?.uid) {
      return null;
    }
    
    // Check if message is read by other participant
    const otherParticipantId = conversation.participants.find(
      id => id !== currentUser?.uid
    );
    
    const isRead = conversation.unreadCount?.[otherParticipantId] === 0;
    
    return isRead ? (
      <CheckCheck size={14} className="text-blue-500" />
    ) : (
      <Check size={14} className="text-gray-400" />
    );
  };
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Open a conversation
  const openConversation = (conversationId) => {
    navigate(`/messages/${conversationId}`, { replace: false });
  };
  
  // Go back to conversation list (mobile)
  const goBackToList = () => {
    navigate('/messages');
  };
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    // Search is handled with the filteredConversations variable
  };
  
  // Handle file upload
  const handleFileUpload = async (file, fileType) => {
    if (!conversationId) return;
    
    try {
      setFileUploading(true);
      
      // Send message with file attachment
      await messagingService.sendMessageWithFile(conversationId, '', file, fileType);
      
      toast.success('File sent successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Failed to upload file');
      throw error;
    } finally {
      setFileUploading(false);
    }
  };
  
  // Handle sending a new message
  const handleSendMessage = async (messageContent) => {
    if (!messageContent.trim() || !currentUser || !conversationId) return;
    
    if (isBlocked) {
      toast.error('You cannot send messages to this user');
      return;
    }
    
    try {
      setSending(true);
      await messagingService.sendMessage(conversationId, messageContent.trim());
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle dismissing the unconnected warning
  const handleDismissWarning = () => {
    setShowUnconnectedWarning(false);
  };
  
  // Handle blocking a user
  const handleBlockUser = () => {
    setIsBlocked(true);
    setShowUnconnectedWarning(false);
    toast.success('User has been blocked');
  };
  
  // Get the other participant of the active conversation
  const activeParticipant = activeConversation ? getOtherParticipant(activeConversation) : null;

  // Group messages by date
  const messageGroups = groupMessagesByDate(messages);

  return (
    <div className="flex h-[calc(100vh-106px)] md:h-[calc(100vh-74px)] bg-white pb-20 md:pb-0">
      {/* Left panel - Conversations list */}
      <div className={`w-full md:w-1/3 lg:w-1/4 h-full flex flex-col bg-white border-r border-gray-200 ${
        conversationId ? 'hidden md:flex' : 'flex'
      }`}>
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center">
            <h1 className="text-xl font-semibold">Messages</h1>
          </div>
          <button className="text-blue-500 hover:text-blue-600 focus:outline-none p-2">
            <MoreHorizontal size={20} />
          </button>
        </div>
        
        {/* Search - Fixed */}
        <div className="p-3 border-b border-gray-200 flex-shrink-0">
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-100 rounded-full border-none focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={16} className="text-gray-500" />
            </div>
          </form>
        </div>
        
        {/* Conversations list - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {conversationsLoading ? (
            <div className="py-20 text-center">
              <Loader className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
              <p className="text-gray-500 text-sm">Loading conversations...</p>
            </div>
          ) : conversationsError ? (
            <div className="py-20 text-center px-4">
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
              <p className="text-red-500 font-medium mb-2 text-sm">Error loading conversations</p>
              <p className="text-gray-500 text-sm">{conversationsError}</p>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="py-20 text-center px-4">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <MessageSquare className="h-8 w-8 text-gray-400" />
              </div>
              {conversations.length === 0 ? (
                <>
                  <h3 className="text-gray-800 font-medium mb-1 text-sm">No conversations yet</h3>
                  <p className="text-gray-500 text-sm mb-4">
                    Connect with students to start chatting
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-gray-800 font-medium mb-1 text-sm">No matching conversations</h3>
                  <p className="text-gray-500 text-sm">
                    Try adjusting your search query
                  </p>
                </>
              )}
            </div>
          ) : (
            <div>
              {filteredConversations.map(conversation => {
                const otherParticipant = getOtherParticipant(conversation);
                const unreadCount = conversation.unreadCount?.[currentUser?.uid] || 0;
                const isActive = conversation.id === conversationId;
                
                return (
                  <div 
                    key={conversation.id}
                    onClick={() => openConversation(conversation.id)}
                    className={`px-4 py-3 flex items-center hover:bg-gray-50 cursor-pointer transition-colors ${
                      isActive ? 'bg-gray-100' : ''
                    } ${unreadCount > 0 ? 'font-medium' : ''}`}
                  >
                    {/* Profile picture */}
                    <div className="relative flex-shrink-0 mr-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden border border-gray-200">
                        {otherParticipant?.photo ? (
                          <img 
                            src={otherParticipant.photo} 
                            alt={otherParticipant.name || 'User'} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-200 to-green-300 flex items-center justify-center text-green-700 font-medium">
                            {otherParticipant?.name?.charAt(0) || '?'}
                          </div>
                        )}
                      </div>
                      {conversation.isOnline && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>
                    
                    {/* Conversation info */}
                    <div className="flex-grow min-w-0">
                      <div className="flex justify-between items-center">
                        <h3 className={`text-sm truncate ${unreadCount > 0 ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>
                          {otherParticipant?.name || 'User'}
                        </h3>
                        <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                          {formatSimpleTime(conversation.lastMessageTimestamp)}
                        </span>
                      </div>
                      
                      <div className="flex items-center mt-1">
                        <p className={`text-xs truncate flex-grow min-w-0 ${unreadCount > 0 ? 'text-gray-900' : 'text-gray-500'}`}>
                          {conversation.lastMessage ? (
                            <>
                              {conversation.lastMessage.senderId === currentUser?.uid && (
                                <span className="mr-1">You:</span>
                              )}
                              {conversation.lastMessage.hasAttachment && (
                                <Paperclip size={12} className="inline mr-1" />
                              )}
                              {conversation.lastMessage.content}
                            </>
                          ) : (
                            <span className="italic">No messages yet</span>
                          )}
                        </p>
                        
                        <div className="flex items-center ml-2">
                          {getReadStatusIcon(conversation)}
                          
                          {unreadCount > 0 && (
                            <span className="ml-2 bg-blue-500 text-white text-xs rounded-full h-5 min-w-5 flex items-center justify-center px-1">
                              {unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Right panel - Active conversation */}
      <div className={`w-full md:w-2/3 lg:w-3/4 h-full flex flex-col bg-white ${
        !conversationId ? 'hidden md:flex' : 'flex'
      }`}>
        {!conversationId ? (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center px-4">
              <div className="w-20 h-20 bg-gradient-to-br from-green-200 to-green-300 rounded-full flex items-center justify-center mx-auto mb-4 text-green-700">
                <MessageSquare className="h-10 w-10" />
              </div>
              <h2 className="text-xl font-medium text-gray-800 mb-2">Your Messages</h2>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                Send private messages to your connections for one-on-one conversations
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Conversation header - Fixed */}
            <div className="flex items-center justify-between px-4 h-16 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center flex-1 min-w-0">
                {/* Mobile back button */}
                <button
                  onClick={goBackToList}
                  className="md:hidden mr-2 text-gray-500 hover:text-gray-700 focus:outline-none flex-shrink-0"
                >
                  <ArrowLeft size={20} />
                </button>
                
                {/* Profile */}
                <div className="flex items-center min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-full overflow-hidden mr-3 border border-gray-200 relative flex-shrink-0">
                    {activeParticipant?.photo ? (
                      <img 
                        src={activeParticipant.photo} 
                        alt={activeParticipant.name || 'User'} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-200 to-green-300 flex items-center justify-center text-green-700 font-medium">
                        {activeParticipant?.name?.charAt(0) || '?'}
                      </div>
                    )}
                    
                    {/* Connection status indicator */}
                    {activeConversation?.connectionStatus?.isConnected && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" 
                           title="Connected"></div>
                    )}
                  </div>
                  
                  <div className="min-w-0 flex-1">
                    <h2 className="font-medium text-gray-900 truncate">
                      {activeParticipant?.name || 'User'}
                    </h2>
                    {activeParticipant?.username && (
                      <p className="text-xs text-gray-500 truncate">
                        @{activeParticipant.username}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action buttons - Hide some on mobile */}
              <div className="flex items-center space-x-2 md:space-x-4 flex-shrink-0">
                <button className="text-gray-500 hover:text-gray-700 focus:outline-none p-2">
                  <Phone size={20} />
                </button>
                <button className="hidden sm:block text-gray-500 hover:text-gray-700 focus:outline-none p-2">
                  <Video size={20} />
                </button>
                <button className="hidden sm:block text-gray-500 hover:text-gray-700 focus:outline-none p-2">
                  <Info size={20} />
                </button>
              </div>
            </div>
            
            {/* Unconnected User Warning Banner */}
            {showUnconnectedWarning && activeConversation && activeParticipant && (
              <UnconnectedUserBanner
                conversation={activeConversation}
                otherParticipant={activeParticipant}
                onDismiss={handleDismissWarning}
                onBlock={handleBlockUser}
              />
            )}
            
            {/* Messages - Scrollable area */}
            <div 
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto px-2 md:px-3 py-2 bg-gray-50 scroll-smooth"
            >
              {/* Sticky date header */}
              {showStickyDate && (
                <div className="sticky top-2 z-10 flex justify-center mb-2 pointer-events-none">
                  <div className="bg-gray-800 bg-opacity-75 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                    {stickyDate}
                  </div>
                </div>
              )}
            
              {messagesLoading && messages.length === 0 ? (
                <div className="py-20 text-center">
                  <Loader className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-500 text-sm">Loading messages...</p>
                </div>
              ) : messagesError ? (
                <div className="py-20 text-center px-4">
                  <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
                  <p className="text-red-500 font-medium text-sm mb-2">Error loading messages</p>
                  <p className="text-gray-500 text-sm">{messagesError}</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="py-20 text-center px-4">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <Send className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-gray-800 font-medium text-sm mb-1">No messages yet</h3>
                  <p className="text-gray-500 text-sm mb-4">
                    Send a message to start the conversation
                  </p>
                </div>
              ) : (
                <div className="space-y-5 pb-2">
                  {messageGroups.map((group, groupIndex) => (
                    <div key={group.date.toString()} className="space-y-3">
                      {/* Date header */}
                      <div className="flex justify-center mb-2" data-message-date={formatMessageDate(group.date)}>
                        <div className="bg-gray-200 text-gray-500 text-xs px-3 py-1 rounded-full">
                          {formatMessageDate(group.date)}
                        </div>
                      </div>
                      
                      {/* Messages for this date */}
                      {group.messages.map((message, index) => {
                        const isCurrentUser = message.senderId === currentUser?.uid;
                        const showAvatar = index === 0 || 
                          group.messages[index - 1].senderId !== message.senderId;
                        const isLastInGroup = 
                          index === group.messages.length - 1 || 
                          group.messages[index + 1].senderId !== message.senderId;
                        
                        return (
                          <div 
                            key={message.id} 
                            className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                          >
                            {!isCurrentUser && showAvatar && (
                              <div className="flex-shrink-0 mr-2 self-end">
                                <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200">
                                  {activeParticipant?.photo ? (
                                    <img 
                                      src={activeParticipant.photo} 
                                      alt={activeParticipant.name || 'User'} 
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-green-200 to-green-300 flex items-center justify-center text-green-700 text-xs font-medium">
                                      {activeParticipant?.name?.charAt(0) || '?'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <div className={`max-w-[80%] sm:max-w-[65%] ${!isCurrentUser && !showAvatar ? 'ml-10' : ''}`}>
                              <div 
                                className={`px-4 py-2 text-sm relative ${
                                  isCurrentUser 
                                    ? 'bg-green-500 text-white rounded-3xl rounded-tr-none shadow-sm' 
                                    : 'bg-white border border-gray-100 text-gray-800 rounded-3xl rounded-tl-none shadow-sm'
                                }`}
                              >
                               {/* Message content */}
                                {message.content && (
                                  <div className={`break-words ${message.attachment ? 'mb-2' : ''}`}>{message.content}</div>
                                )}
                                
                                {/* File attachment */}
                                {message.attachment && (
                                  <MessageAttachment
                                    attachment={message.attachment}
                                    isCurrentUser={isCurrentUser}
                                    compact={false}
                                  />
                                )}
                                
                                {/* Show warning icon for unconnected messages */}
                                {message.isFromUnconnectedUser && !isCurrentUser && (
                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center" 
                                       title="Message from unconnected user">
                                    <AlertCircle size={10} className="text-white" />
                                  </div>
                                )}
                              </div>
                              
                              {isLastInGroup && (
                                <div className={`flex items-center mt-1 text-xs text-gray-500 ${
                                  isCurrentUser ? 'justify-end' : 'justify-start'
                                }`}>
                                  <span>{formatSimpleTime(message.timestamp)}</span>
                                  
                                  {isCurrentUser && (
                                    <span className="ml-1">
                                      {message.isRead ? (
                                        <CheckCheck size={12} className="text-blue-500" />
                                      ) : (
                                        <Check size={12} />
                                      )}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
            
            {/* Enhanced Message Input - Fixed at bottom */}
            <div className="flex-shrink-0">
              <EnhancedMessageInput
                value={newMessage}
                onChange={setNewMessage}
                onSend={handleSendMessage}
                onFileUpload={handleFileUpload}
                disabled={isBlocked}
                sending={sending || fileUploading}
                placeholder={isBlocked ? "You cannot send messages to this user" : "Message..."}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;